// components/nft/nft-form.tsx
import { Plus, Trash2 } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { NFTFormData } from '@/types/nft';

interface NFTFormProps {
  formData: NFTFormData;
  onChange: (data: NFTFormData) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isEditing?: boolean;
  isLoading?: boolean;
}

export const NFTForm: React.FC<NFTFormProps> = ({
  formData,
  onChange,
  onSubmit,
  onCancel,
  isEditing = false,
  isLoading = false,
}) => {
  const updateFormField = (field: keyof NFTFormData, value: any) => {
    onChange({
      ...formData,
      [field]: value,
    });
  };

  const addAttribute = () => {
    const newAttributes = [
      ...formData.attributes,
      { trait_type: '', value: '' },
    ];
    updateFormField('attributes', newAttributes);
  };

  const updateAttribute = (
    index: number,
    field: 'trait_type' | 'value',
    newValue: string,
  ) => {
    const newAttributes = formData.attributes.map((attr, i) =>
      i === index ? { ...attr, [field]: newValue } : attr,
    );
    updateFormField('attributes', newAttributes);
  };

  const removeAttribute = (index: number) => {
    const newAttributes = formData.attributes.filter((_, i) => i !== index);
    updateFormField('attributes', newAttributes);
  };

  const isFormValid = formData.name && formData.collection && formData.price;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => updateFormField('name', e.target.value)}
            placeholder="Enter NFT name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="collection">Collection *</Label>
          <Input
            id="collection"
            value={formData.collection}
            onChange={(e) => updateFormField('collection', e.target.value)}
            placeholder="Enter collection name"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => updateFormField('description', e.target.value)}
          placeholder="Describe your NFT..."
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="price">Price *</Label>
          <Input
            id="price"
            type="number"
            step="0.001"
            value={formData.price}
            onChange={(e) => updateFormField('price', e.target.value)}
            placeholder="0.00"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="currency">Currency</Label>
          <Select
            value={formData.currency}
            onValueChange={(value) => updateFormField('currency', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ETH">ETH</SelectItem>
              <SelectItem value="SOL">SOL</SelectItem>
              <SelectItem value="MATIC">MATIC</SelectItem>
              <SelectItem value="USDC">USDC</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="image">Image URL</Label>
        <Input
          id="image"
          value={formData.image}
          onChange={(e) => updateFormField('image', e.target.value)}
          placeholder="https://example.com/image.png"
        />
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label>Attributes</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addAttribute}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Attribute
          </Button>
        </div>

        {formData.attributes.map((attribute, index) => (
          <div key={index} className="flex gap-2 items-start">
            <div className="flex-1">
              <Input
                placeholder="Trait type (e.g., Background)"
                value={attribute.trait_type}
                onChange={(e) =>
                  updateAttribute(index, 'trait_type', e.target.value)
                }
              />
            </div>
            <div className="flex-1">
              <Input
                placeholder="Value (e.g., Blue)"
                value={attribute.value}
                onChange={(e) =>
                  updateAttribute(index, 'value', e.target.value)
                }
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => removeAttribute(index)}
              disabled={formData.attributes.length === 1}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex gap-3 pt-4">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!isFormValid || isLoading}
          className="flex-1"
        >
          {isLoading ? 'Saving...' : isEditing ? 'Save Changes' : 'Create NFT'}
        </Button>
      </div>
    </div>
  );
};
